<template>
  <div data-locator="data">
    <div class="text-body-1 font-weight-bold mb-3 ml-1">
      {{ $t('project.project-settings.ProjectSettingsDataRoomInfo.title') }}
    </div>
    <AppCard class="pa-8 ma-1" data-locator="data-table">
      <template v-if="roomStats === null">
        <v-row :class="{'my-4': $vuetify.display.xs}">
          <v-col cols="12" sm="4">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" sm="4">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
          <v-col cols="12" sm="4">
            <v-skeleton-loader type="text@3"></v-skeleton-loader>
          </v-col>
        </v-row>
      </template>
      <template v-else>
        <v-row :class="{'my-4': $vuetify.display.xs}">
          <v-col :class="{ 'pb-8' : $vuetify.display.xs, 'pr-8' : $vuetify.display.smAndUp}"
                 class="d-flex flex-column row-gap-4"
                 cols="12"
                 sm="4"
                 data-locator="additional-informations-column"
          >
            <p class="d-flex align-center column-gap-2 mb-4 text--medium text--bold">
              <font-awesome-icon :icon="['far', 'list-alt']" />
              {{ $t('project.project-settings.ProjectSettingsDataRoomInfo.additionalInformation') }}
            </p>
            <ul class="settings-list">
              <li>
                <span class="text--small">{{ $t('project.project-settings.ProjectSettingsDataRoomInfo.createdAt') }}</span>
                <span class="text--small text--bold"><DateDisplay :value="currentRoom.createdAt" /></span>
              </li>
              <li>
                <span class="text--small">{{ $t('project.project-settings.ProjectSettingsDataRoomInfo.duration') }}</span>
                <span
                          class="text--small text--bold"
                          v-t="{
                            path: 'project.project-settings.ProjectSettingsDataRoomInfo.months',
                            plural: currentRoom.dataroomInfo.initialDuration,
                            args: { count: currentRoom.dataroomInfo.initialDuration },
                          }"
                />
              </li>
              <li>
                <span class="text--small">{{ $t('project.project-settings.ProjectSettingsDataRoomInfo.renewal') }}</span>
                <span class="text--small text--bold"><DateDisplay :value="currentRoom.dataroomInfo.renewalDate"/></span>
              </li>
            </ul>
          </v-col>
          <v-col :class="{dividerH : $vuetify.display.xs, dividerV : $vuetify.display.smAndUp}"
                 class="d-flex flex-column row-gap-4"
                 cols="12"
                 sm="4"
                 data-locator="documents-column"
          >
            <p class="d-flex align-center column-gap-2 mb-4 text--medium text--bold">
              <font-awesome-icon :icon="['far', 'file-alt']" />
              {{ $t('project.project-settings.ProjectSettingsDataRoomInfo.documents') }}
            </p>
            <ul class="settings-list">
              <li>
                <span class="text--small">{{ $t('project.project-settings.ProjectSettingsDataRoomInfo.documentsCountText') }}</span>
                <span
                          class="text--small text--bold"
                          v-t="{
                            path: 'project.project-settings.ProjectSettingsDataRoomInfo.documentsCount',
                            plural: files,
                            args: { count: files },
                          }"
                />
              </li>
              <li>
                <span class="text--small">{{ $t('project.project-settings.ProjectSettingsDataRoomInfo.archivedDocumentsCountText') }}</span>
                <span
                          class="text--small text--bold"
                          v-t="{
                            path: 'project.project-settings.ProjectSettingsDataRoomInfo.documentsCount',
                            plural: archivedDocuments,
                            args: { count: archivedDocuments },
                          }"
                />
              </li>
            </ul>
          </v-col>
          <v-col :class="{dividerH : $vuetify.display.xs, dividerV : $vuetify.display.smAndUp}"
                 class="d-flex flex-column row-gap-4"
                 cols="12"
                 sm="4"
                 data-locator="storage-column"
          >
            <p class="d-flex align-center column-gap-2 mb-4 text--medium text--bold">
              <font-awesome-icon :icon="['far', 'database']" />
              {{ $t('project.project-settings.ProjectSettingsDataRoomInfo.storage') }}
            </p>
            <ul class="settings-list mb-2">
              <li class="text--small" data-locator="room-size">
                <span data-locator="size-title">
                  {{ dataroomAccessTypePPD
                        ? $t('project.project-settings.ProjectSettingsDataRoomInfo.dataRoomSizeLabel')
                        : $t('project.project-settings.ProjectSettingsDataRoomInfo.storageDataLabel')
                  }}

                  <AppInfoTooltip
                    v-if="currentRoom.documentRedactionEnabled"
                    icon-size="13"
                    location="top"
                  >
                    {{ $t('project.project-settings.ProjectSettingsDataRoomInfo.redactStorageWarning') }}
                  </AppInfoTooltip>
                </span>

                <template v-if="roomStatsPending">
                  <v-skeleton-loader type="text" min-width="120" />
                </template>
                <template v-else>
                  <span data-locator="size-number">
                    <span v-if="dataroomAccessTypePPD"
                      class="text--small text--bold"
                    >
                      {{ computedDisplayStorage }}
                    </span>
                    <span v-else
                          class="text--small text--bold"
                          
                    >
                        {{ this.usedStorage.storage }} {{ this.$t(`project.project-settings.ProjectSettingsDataRoomInfo.${this.usedStorage.unit}`) }}
                    </span>
                  </span>
                </template>
              </li>
            </ul>
            <v-progress-linear :model-value="storagePercent"
                               v-if="dataroomAccessTypePPD"
                               color="blue"
                               height="10"
                               rounded
            />
            <app-button v-if="isUpgradeButtonDisplayed && dataroomAccessTypePPD"
                        class="mr-auto mt-4"
                        type="outlined"
                        data-locator="increate-storage-button"
                        @click="onUpgrade"
            >
              {{ $t(`project.project-settings.ProjectSettingsDataRoomInfo.upgradeButtonLabel`)}}
            </app-button>
          </v-col>
        </v-row>
      </template>
    </AppCard>

    <ProjectSettingsDataroomUpgradeDialog v-if="isDataroomUpgradeDialogOpened"
                                          @close="isDataroomUpgradeDialogOpened = false"
                                          @refresh-stats="prepareGetRoomStats"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import { ROOM_TYPES } from '@/common/utils/roomTypes'
import { toast } from '@/common/utils/toast'
import { GET_ROOM_STATS } from '@/store/modules/room/action_types'

import ProjectSettingsDataroomUpgradeDialog from './ProjectSettingsDataroomUpgradeDialog.vue'

export default {
  name: 'ProjectSettingsDataRoomInfo',
  components: {
    AppCard,
    AppInfoTooltip,
    DateDisplay,
    ProjectSettingsDataroomUpgradeDialog,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
    showUpgradeDialog: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      isDataroomUpgradeDialogOpened: false,
    }
  },
  computed: {
    ...mapState('room', ['currentRoom', 'roomStats', 'roomStatsPending']),
    ...mapGetters('room', [
      'archivedDocuments',
      'files',
      'storage',
      'totalStorage',
      'usedStorage',
      'storagePercent',
      'isPmOfRoomSubscription',
    ]),
    computedDisplayStorage () {
      return this.$t('project.project-settings.ProjectSettingsDataRoomInfo.storageData',
        {
          totalCount: this.totalStorage.storage,
          totalUnit: this.$t(`project.project-settings.ProjectSettingsDataRoomInfo.${this.totalStorage.unit}`),
          usedCount: this.usedStorage.storage,
          usedUnit: this.$t(`project.project-settings.ProjectSettingsDataRoomInfo.${this.usedStorage.unit}`),
        },
      )
    },
    isUpgradeButtonDisplayed () {
      return this.currentRoom.type !== ROOM_TYPES.DATAROOM_UNLIMITED && this.isPmOfRoomSubscription
    },
    dataroomAccessTypePPD () {
      return this.currentRoom.type === 'PPD'
    },
  },
  mounted () {
    this.prepareGetRoomStats()
  },
  methods: {
    ...mapActions('room', [GET_ROOM_STATS]),
    onUpgrade () {
      this.isDataroomUpgradeDialogOpened = true
    },
    async prepareGetRoomStats () {
      try {
        await this.GET_ROOM_STATS(this.mnemo)
      } catch (error) {
        toast.error(this.$t('project.project-settings.ProjectSettingsDataRoomInfo.getRoomStatsError'))
      }
    },
  },
  watch: {
    showUpgradeDialog (newValue) {
      this.isDataroomUpgradeDialogOpened = newValue
    },
    '$route.query': function () {
      if (this.$route.query.show) {
        this.isDataroomUpgradeDialogOpened = true
      }
    },
  },
}
</script>

<style scoped lang="scss">
.dividerV {
  padding: 8px 30px;
  border-left: 1px solid #E0E0E0;

  &:last-child {
    padding-right: 12px;
  }
}

.dividerH {
  padding: 30px 8px;
  border-top: 1px solid #E0E0E0;

  &:last-child {
    padding-bottom: 0;
  }
}

.settings-list {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  row-gap: 16px;

  li {
    display: flex;
    justify-content: space-between;
  }
}
</style>
